import { NgModule, APP_INITIALIZER } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Routes } from '@angular/router';

// external modules
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UiSwitchModule } from 'ngx-ui-switch';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { NgxsModule } from '@ngxs/store';

// bayclubs module
import { SharedModule } from '../shared/shared.module';
import { UtilsModule, PRIVILEGES_PROVIDERS } from '@bayclubs/utils';
import { DropDownModule } from '@bayclubs/drop-down';
import { BcRecurringModule } from '@bayclubs/recurring';
import { bcCalendarConst, BcCalendarModule } from '@bayclubs/calendar';
import { BayclubsUiTimePickerModule } from '@bayclubs/ui-time-picker';

// application services
import { InfrastructureModule } from '../infrastructure/infrastructure.module';
import { CalendarEventsService } from './components/calendar/calendar-events.service';

//event-handlers
import { OnAddEditGroupClassEventHandler } from './services/event-handlers/on-add-edit-group-class-event.handler';

// state
import { ClassesCalendarState } from './+store/classes-calendar.state';

// application components
import { GroupClassesListComponent } from './routes/group-classes-list/group-classes-list.component';
import { GroupClassesSearchCriteriaComponent } from './routes/group-classes-list/group-classes-search-criteria/group-classes-search-criteria.component';
import { GroupClassesListRowComponent } from './routes/group-classes-list/group-classes-list-row/group-classes-list-row.component';
import { GroupClassSaveComponent } from './components/group-class-save/group-class-save.component';
import { DatesMultiselectModalComponent } from './components/dates-multiselect-modal/dates-multiselect-modal.component';
import { GroupClassesDetailsComponent } from './routes/group-classes-details/group-classes-details.component';
import { GroupClassesDetailsAttendensComponent } from './routes/group-classes-details/group-classes-details-attendens/group-classes-details-attendens.component';
import { GroupClassesDetailsBasicsComponent } from './routes/group-classes-details/group-classes-details-basics/group-classes-details-basics.component';
import { GroupClassesDetailsSettingsComponent } from './routes/group-classes-details/group-classes-details-settings/group-classes-details-settings.component';
import { GroupClassesCategoryIconComponent } from './components/group-classes-category-icon/group-classes-category-icon.component';
import { GroupClassesCalendarComponent } from './routes/group-classes-calendar/group-classes-calendar.component';
import { CalendarSettingsComponent } from './components/calendar/calendar-settings/calendar-settings.component';
import { CalendarEventComponent } from './components/calendar/calendar-event/calendar-event.component';
import { PrivilegesApiService } from './services/privileges.api.service';
import { ManagementPrivilegesProviderService } from './services/management-privileges-provider.service';
import { GcPermissionGuard } from '../infrastructure/services/gc-permission.guard';
import { ManagementContextResolverService } from '../shared/services/management-context-resolver.service';
import { InstructorLabelComponent } from './components/instructor-label/instructor-label.component';
import { InstructorSubstitutionComponent } from './components/instructor-substitution/instructor-substitution.component';
import { GroupClassesAttendanceComponent } from './routes/group-classes-details/group-classes-attendance/group-classes-attendance.component';
import { RealAttendanceSaveComponent } from './routes/group-classes-details/real-attendance-save/real-attendance-save.component';
import { RecurringClassActionConfirmationComponent } from './components/recurring-class-action-confirmation/recurring-class-action-confirmation.component';
import { GroupClassesDetailsWaitListComponent } from './routes/group-classes-details/group-classes-details-wait-list/group-classes-details-wait-list.component';
import { GroupClassesBookingsComponent } from './routes/group-classes-details/group-classes-bookings/group-classes-bookings.component';
import { GroupClassesAddParticipantsComponent } from './routes/group-classes-details/group-classes-add-participants/group-classes-add-participants.component';
import { AddParticipantComponent } from './routes/group-classes-details/add-participant/add-participant.component';
import { SelectInstructorComponent } from './components/select-instructor/select-instructor.component';
import { ClassesCalendarComponent } from './containers/classes-calendar/classes-calendar.component';
import { dualAuthGuard } from '../core/auth/dual-auth.guard';
import { AuthService } from '../core/services/auth.service';
import { NgxMaskDirective } from 'ngx-mask';


export const managementRoutes: Routes = [
    {
        path: 'management/classes/calendar',
        component: GroupClassesCalendarComponent,
        canActivate: [dualAuthGuard, GcPermissionGuard],
        resolve: {
            context: ManagementContextResolverService
        },
        data: {
            expectedPermission: 'GroupClasses.canViewClasses',
            title: 'Calendar',
            containerClass: 'group-classes-calendar-container'
        }
    },
    {
        path: 'management/classes',
        component: GroupClassesListComponent,
        canActivate: [dualAuthGuard, GcPermissionGuard],
        resolve: {
            context: ManagementContextResolverService
        },
        data: {
            expectedPermission: 'GroupClasses.canViewClasses',
            title: 'List'
        }
    },
    {
        path: 'management/classes/:id',
        component: GroupClassesDetailsComponent,
        canActivate: [dualAuthGuard, GcPermissionGuard],
        resolve: {
            context: ManagementContextResolverService
        },
        data: {
            expectedPermission: 'GroupClasses.canViewClasses'
        }
    }
];

export function onModuleInit(auth: AuthService, classEventHandler: OnAddEditGroupClassEventHandler): () => Promise<any> {
    return (): Promise<any> => {
        return new Promise((resolve, reject) => {
            classEventHandler.init();
            resolve(true);
        });
    };
}
@NgModule({
    declarations: [
        GroupClassesListComponent,
        GroupClassesSearchCriteriaComponent,
        GroupClassesListRowComponent,
        GroupClassSaveComponent,
        DatesMultiselectModalComponent,
        GroupClassesDetailsComponent,
        GroupClassesDetailsAttendensComponent,
        GroupClassesDetailsBasicsComponent,
        GroupClassesDetailsSettingsComponent,
        GroupClassesCategoryIconComponent,
        GroupClassesCalendarComponent,
        CalendarSettingsComponent,
        CalendarEventComponent,
        InstructorLabelComponent,
        InstructorSubstitutionComponent,
        GroupClassesAttendanceComponent,
        RealAttendanceSaveComponent,
        RecurringClassActionConfirmationComponent,
        GroupClassesDetailsWaitListComponent,
        GroupClassesBookingsComponent,
        GroupClassesAddParticipantsComponent,
        AddParticipantComponent,
        SelectInstructorComponent,
        ClassesCalendarComponent,
    ],
    imports: [
        CommonModule,
        RouterModule.forChild(managementRoutes),
        FormsModule,
        ReactiveFormsModule,
        TranslateModule.forChild(),
        NgxsModule.forFeature([ClassesCalendarState]),
        TooltipModule,
        TabsModule,
        UtilsModule,
        BcCalendarModule,
        InfrastructureModule,
        UiSwitchModule,
        TypeaheadModule,
        BsDropdownModule.forRoot(),
        PopoverModule.forRoot(),
        SharedModule,
        DropDownModule,
        BcRecurringModule,
        NgxMaskDirective,
        BayclubsUiTimePickerModule
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: onModuleInit,
            multi: true,
            deps: [AuthService, OnAddEditGroupClassEventHandler]
        },
        OnAddEditGroupClassEventHandler,
        CalendarEventsService,
        PrivilegesApiService,
        {
            provide: PRIVILEGES_PROVIDERS,
            useClass: ManagementPrivilegesProviderService,
            multi: true
        }
    ]
})
export class ManagementModule {
    constructor() {
        bcCalendarConst.autoModeColumnWidth = 220;
    }
}
